import React from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { Tooltip, Button, Modal, Icon } from 'antd';
import cn from 'classnames';
import moment from 'moment';
import DOMPurify from 'dompurify';
import useQuestionMsgAttachList from '../../queries/useQuestionMsgAttachList';
import useSortAttachmentsByExtensions from '../../../../hooks/useSortAttachmentsByExtension';
import ImagesGallery from '../ImagesGallery/ImagesGallery';
import useMessageImgPreview from '../../queries/useMessageImgPreview';
import DownloadAttach from '../DownloadAttach/DownloadAttach';
import useMessageAttachment from '../../queries/useMessageAttachment';
import useDeleteMsgMutation from '../../mutations/useDeleteMsgMutation';
import { questionsQueryKeys } from '../../queries/questionsQueryKeys';
import { selectedQuestionSelector } from '../../store/selectors';
import useMsgNeedAnswerMutation from '../../mutations/useMsgNeedAnswerMutation';
import { questionOpenStateId } from '../../constants/common';
import useQuestionsQueryParams from '../../hooks/useQuestionsQueryParams';
import styles from './Message.module.scss';

const { confirm } = Modal;

const addBlankTargetToLinks = (node) => {
  if (node.tagName && node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
};

DOMPurify.addHook('beforeSanitizeAttributes', addBlankTargetToLinks);

const Message = ({ message, isLast }) => {
  const {
    id,
    fromClientNote,
    note,
    companySignature,
    worker,
    date,
    canBeDeleted,
    needAnswer,
    isHtml,
  } = message;

  const questionsQueryParams = useQuestionsQueryParams();
  const queryClient = useQueryClient();
  const selectedQuestion = useSelector(selectedQuestionSelector);

  const { data: attachments } = useQuestionMsgAttachList(message.id, {
    enabled: message.hasAttachment,
  });

  const { images, files } = useSortAttachmentsByExtensions({ attachments });

  const handleDeleteMsgSuccess = (data) => {
    if (data !== 'Error') {
      queryClient.setQueryData(
        questionsQueryKeys.questionMessages(selectedQuestion.id),
        (oldMessages) => oldMessages.filter((msg) => msg.id !== id)
      );
    }
  };

  const handleNeedAnswerChangeSuccess = (data) => {
    if (data !== 'Error') {
      queryClient.setQueryData(
        questionsQueryKeys.questionMessages(selectedQuestion.id),
        (oldMessages) =>
          oldMessages.map((msg) => {
            if (msg.id === id) {
              return { ...msg, needAnswer: !needAnswer };
            }

            return msg;
          })
      );

      queryClient.invalidateQueries(
        questionsQueryKeys.questions(questionsQueryParams)
      );
    }
  };

  const { mutateAsync: deleteMsg } = useDeleteMsgMutation({
    onSuccess: handleDeleteMsgSuccess,
  });

  const { mutate: needAnswerChange, isLoading: isNeedAnswerChangeLoading } =
    useMsgNeedAnswerMutation({
      onSuccess: handleNeedAnswerChangeSuccess,
    });

  const handleDeleteMsgBtnClick = () => {
    confirm({
      title: 'Удалить комментарий?',
      onOk: async () => {
        await deleteMsg({ questionNoteId: id });
      },
      onCancel: () => {},
    });
  };

  const handleNeedAnswerBtnClick = () => {
    needAnswerChange({ questionNoteId: id, needAnswer: !needAnswer });
  };

  return (
    <div
      className={cn(styles.root, {
        [styles.clientMsg]: fromClientNote,
        [styles.localMsg]: !fromClientNote,
      })}
    >
      <ImagesGallery
        images={images}
        queryUrl="/Question/note_attachment"
        queryParams={{ questionNoteId: id }}
        useImgQuery={useMessageImgPreview}
        queryFnData={{ questionNoteId: id }}
      />
      {files.length > 0 && (
        <div className={styles.filesWrap}>
          {files.map((file) => (
            <DownloadAttach
              attachData={file}
              key={file.id}
              useAttachQuery={useMessageAttachment}
              queryFnData={{ questionNoteId: id }}
            />
          ))}
        </div>
      )}
      {isHtml ? (
        <div
          className={styles.htmlWrap}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(note, { ADD_ATTR: ['target'] }),
          }}
        />
      ) : (
        <span className={styles.note}>{note || ''}</span>
      )}

      <div className={styles.footer}>
        {companySignature && (
          <Tooltip placement="right" title="Получатель не видит имя сотрудника">
            <p className={styles.footer}>
              <span>{worker?.serviceCompanyName}</span>
              <span style={{ opacity: 0.5 }}>{`(${
                worker?.fullName?.trim() || ''
              })`}</span>
            </p>
          </Tooltip>
        )}

        {!companySignature && worker?.fullName?.trim() && (
          <span>{worker.fullName.trim()}</span>
        )}

        {date && <span>{moment(date).format('DD.MM.YY HH:mm')}</span>}
      </div>

      {canBeDeleted && (
        <Tooltip title="Удалить">
          <Button
            className={cn(styles.action, styles.deleteAction)}
            shape="circle"
            type="ghost"
            size="small"
            icon="delete"
            onClick={handleDeleteMsgBtnClick}
          />
        </Tooltip>
      )}

      {isLast &&
        fromClientNote &&
        selectedQuestion?.stateId === questionOpenStateId && (
          <Tooltip
            title={needAnswer ? 'Требуется ответ' : 'Ответ не требуется'}
          >
            <Button
              shape="circle"
              type="ghost"
              size="small"
              className={styles.action}
              onClick={handleNeedAnswerBtnClick}
            >
              <Icon
                style={{ fontSize: 18, marginTop: 1 }}
                type={
                  isNeedAnswerChangeLoading
                    ? 'loading'
                    : needAnswer
                    ? 'message'
                    : 'check-circle'
                }
                theme={needAnswer ? 'filled' : 'outlined'}
              />
            </Button>
          </Tooltip>
        )}
    </div>
  );
};

export default Message;
